import { DestroyRef, inject, Injectable, OnDestroy } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { Capacitor } from "@capacitor/core";
import { BehaviorSubject, distinctUntilChanged } from "rxjs";

import { LocalStorage, SessionStorage } from "@dffedb/util";

import { AppRatingService } from "./app-rating.service";
import { AktuelForsyningIndstillingerObserver } from "../forsyning-indstillinger/aktuel-forsyning-indstillinger.observer";

@Injectable({ providedIn: "root" })
export class AppRatingInteractionTrackerService implements OnDestroy {
    private readonly promptDelayMs = 90000; // 90 seconds
    private readonly sessionDismissedKey = "appReview.bannerDismissedSession";
    private readonly neverShowAgainKey = "appReview.bannerNeverShowAgain";

    private readonly showBannerSubject = new BehaviorSubject<boolean>(false);
    public readonly showBanner$ = this.showBannerSubject.asObservable();

    private started = false;
    private timerArmed = false;
    private promptTimerId: ReturnType<typeof setTimeout> | null = null;

    private readonly appRatingService = inject(AppRatingService);
    private readonly aktuelForsyningIndstillinger = inject(AktuelForsyningIndstillingerObserver);
    private readonly destroyRef = inject(DestroyRef);
    private readonly sessionStorage = inject(SessionStorage);
    private readonly localStorage = inject(LocalStorage);

    public start(): void {
        if (this.started) {
            return;
        }

        if (!Capacitor.isNativePlatform()) {
            return;
        }

        if (this.isDismissedForSession() || this.isNeverShowAgain()) {
            return;
        }

        this.started = true;

        this.aktuelForsyningIndstillinger.isPilot.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((isPilot) => {
            if (isPilot) {
                this.armPromptTimer();
            } else {
                this.clearPromptTimer();
                this.showBannerSubject.next(false);
            }
        });
    }

    public async confirmAndRequestReview(): Promise<void> {
        const didRequestReview = await this.appRatingService.requestReviewFromUserAction();
        if (didRequestReview) {
            this.dismissForSession();
        } else {
            this.showBannerSubject.next(false);
        }
    }

    public dismissForSession(): void {
        this.sessionStorage.setItem(this.sessionDismissedKey, "1");
        this.showBannerSubject.next(false);
    }

    public dismissForever(): void {
        this.localStorage.setItem(this.neverShowAgainKey, "1");
        this.dismissForSession();
    }

    public ngOnDestroy(): void {
        this.clearPromptTimer();
    }

    private armPromptTimer(): void {
        if (this.timerArmed || this.isDismissedForSession() || this.isNeverShowAgain()) {
            return;
        }

        this.timerArmed = true;
        this.promptTimerId = setTimeout(() => {
            const shouldShowBanner = this.appRatingService.markPromptShownIfEligible();
            if (shouldShowBanner && !this.isDismissedForSession() && !this.isNeverShowAgain()) {
                this.showBannerSubject.next(true);
            }
        }, this.promptDelayMs);
    }

    private clearPromptTimer(): void {
        if (this.promptTimerId) {
            clearTimeout(this.promptTimerId);
            this.promptTimerId = null;
        }
        this.timerArmed = false;
    }

    private isDismissedForSession(): boolean {
        return this.sessionStorage.getItem(this.sessionDismissedKey) === "1";
    }

    private isNeverShowAgain(): boolean {
        return this.localStorage.getItem(this.neverShowAgainKey) === "1";
    }
}
